import { call, put, select, takeEvery } from 'redux-saga/effects';

import { actions as userActions, selectors as userSelectors } from '@elab/core/store/user';
import { actions as uiActions } from '@elab/core/store/ui';
import { actions as globalActions } from '@elab/core/store/global';
import {
    editSavedCardErrorMessage,
    physicalCardAddedSuccessMessage,
} from '@elab/core/utils/constants';
import { loadPinCodeRequired } from '@elab/components/connected/forms/PinCodeForm/api';

import api from './api';

import { actions, selectors, types } from '.';

const URL = '/my-account/loyalty';

function* submitCardDetailsWithoutPinCode() {
    const cardNumber = yield select(selectors.getCardNumber, 'myAccount');
    const dateOfBirth = yield select(userSelectors.getDateOfBirth);
    try {
        const response = yield call(api.submitCardDetails, {
            cardNumber,
            activationCode: dateOfBirth,
        });
        if (response) {
            yield put(actions.setDataAttr('showValidationError', ''));
            yield put(actions.setDataAttr('apiError', false));
            yield put(globalActions.redirect({ successRedirectUrl: URL }));
            const message = {
                ...physicalCardAddedSuccessMessage,
                delay: '5000',
            };
            yield put(globalActions.addMessage(message));
            yield put(userActions.setLoyaltyCardValue(cardNumber));
        }
    } catch (err) {
        yield put(actions.setDataAttr('apiError', err));
    }
}

function* getLoyaltyPointBalance({payload}) {
    const pointBalance = yield call(api.getLoyaltyPointBalance, payload);
    if (payload) {
        yield put(actions.setLoyaltyEmployeePointBalance(pointBalance));
    } else {
        yield put(actions.setLoyaltyPointBalance(pointBalance));
    }
}

export function* doSubmit() {
    const cardNumber = yield select(selectors.getCardNumber, 'myAccount');
    const pinCode = yield select(selectors.getPinCode, 'myAccount');
    const dto = yield call(userDtoCombinator, {});
    const dateOfBirth = yield select(userSelectors.getDateOfBirth);
    yield put(uiActions.clearPincode(false));
    try {
        const isValidPinCode = yield call(api.validatePinCode, {
            loyaltyInformation: { ...dto, cardNumber, pinCode },
        });
        if (isValidPinCode) {
            const response = yield call(api.submitCardDetails, {
                cardNumber,
                pinCode,
                activationCode: dateOfBirth,
            });
            if (response) {
                yield put(uiActions.closeDialog());
                yield put(actions.setDataAttr('showValidationError', ''));
                yield put(actions.setDataAttr('apiError', false));
                yield put(globalActions.redirect({ successRedirectUrl: URL }));
                const message = {
                    ...physicalCardAddedSuccessMessage,
                    delay: '5000',
                };
                yield put(globalActions.addMessage(message));
                yield put(userActions.setLoyaltyCardValue(cardNumber));
            }
        }
    } catch (err) {
        yield put(actions.setDataAttr('apiError', err));
        yield put(uiActions.closeDialog());
        yield put(uiActions.clearPincode(true));
    }
}

export function* userDtoCombinator(dto) {
    let userDto = yield select(userSelectors.getProfile) || dto;
    const { loyaltyInformation } = userDto;
    return loyaltyInformation;
}

export function* setNewCardValue({ payload: { cardNumber } }) {
    const maskValue = yield select(selectors.getCardNumberMask, 'myAccount');
    // eslint-disable-next-line elab-rules/detect-non-literal-regexp
    const regEx = new RegExp(maskValue);
    if (cardNumber && cardNumber.length === 13 && regEx.test(cardNumber)) {
        yield put(actions.setDataAttr('cardNumber', cardNumber));
        yield put(actions.setDataAttr('showValidationError', ''));
    } else {
        yield put(actions.setDataAttr('showValidationError', true));
    }
    yield put(actions.setDataAttr('apiError', false));
}

export function* getCards() {
    yield put(actions.setLoadingSavedCard(false));
    try {
        const response = yield call(api.getSavedCards);
        if (response) {
            yield put(actions.setSavedCards(response));
        }
    } catch (error) {
        yield put(actions.setDataAttr('apiError', error));
        yield put(actions.setValidationError(error));
    } finally {
        yield put(actions.setLoadingSavedCard(true));
    }
}

export function* setPinCodeValue({ payload }) {
    yield put(actions.setDataAttr(payload.name, payload.value));
}

export function* editSavedCard() {
    try {
        const response = yield call(api.getSavedCardIframeURL);
        if (response) {
            const iframeURL = response.redirectURL;
            yield put(
                uiActions.openDialog({
                    name: 'edit-credit-card-popup',
                    iframeUrl: iframeURL,
                    iframeRedirectOnClose: '/my-account/payment-details',
                }),
            );
        }
    } catch (err) {
        const message = {
            ...editSavedCardErrorMessage,
            delay: '5000',
        };
        yield put(globalActions.addMessage(message));
    }
}

export function* fetchFormData() {
    try {
        const response = yield call(api.getFormData);
        if (response) {
            yield put(actions.setDataAttr('lostOrStolenFormData', response.steps[0].fields));
        }
    } catch (err) {
        yield put(actions.setDataAttr('apiError', err));
    }
}

export function* validateNewCard({ payload: { cardNumber } }) {
    try {
        const dto = yield call(userDtoCombinator, { cardNumber });
        const response = yield call(api.validateCardData, { ...dto, cardNumber });
        if (response) {
            const shouldAskPinCode = yield call(loadPinCodeRequired, {
                loyaltyInformation: { cardNumber },
            });
            if (shouldAskPinCode) {
                yield put(uiActions.openDialog({ name: 'pincode-form-popup' }));
            } else {
                yield call(submitCardDetailsWithoutPinCode);
            }
            yield put(actions.setDataAttr('apiError', false));
        }
    } catch (err) {
        yield put(actions.setDataAttr('apiError', err));
    }
}

export default [
    takeEvery(types.VALIDATE_CARD, validateNewCard),
    takeEvery(types.SET_CARD_NUMBER, setNewCardValue),
    takeEvery(types.GET_SAVED_CARD, getCards),
    takeEvery(types.SET_DATA_FIELD_VALUE, setPinCodeValue),
    takeEvery(types.EDIT_SAVED_CARD, editSavedCard),
    takeEvery(types.GET_LOYALTY_POINT_BALANCE, getLoyaltyPointBalance),
    takeEvery(types.FETCH_FORM_DATA, fetchFormData),
    takeEvery(types.SUBMIT, doSubmit),
];
